import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import NavSignOut from "./NavSignOut";
import NavSignOutIcon from "./NavSignOutIcon";
import { UnreadBadge } from "./UnreadBadge";
import logoMark from "@/assets/logo-allround.png";

const GOLD = "#C6A15B", INK = "#201D17", INK_SOFT = "#6E675A", CREAM = "#F7F1E5", SAND = "#EFE6D2";
const LINE = "rgba(198,161,91,0.28)";

function linkStyle(active) {
  return active ? { background: SAND, color: INK } : { color: INK_SOFT, background: "transparent" };
}

/**
 * Shared "AllRound Health Concierge" lux sidebar (gold / ink / cream) — the
 * same flat concierge shell used by the guest workspace, generalised for every
 * back-office persona (Doctor / Hotel / Pharmacy / Lab / Platform Admin).
 */
export default function LuxSidebar({ items, subtitle, footer, testidPrefix, homePath, tabs, activeTabId }) {
  const [open, setOpen] = useState(false);

  const NavList = ({ onNavigate }) => (
    <nav className="flex-1 overflow-y-auto py-4 px-3">
      {items.map((n, i) => {
        const showSection = n.section && n.section !== items[i - 1]?.section;
        return (
          <div key={n.to}>
            {showSection && (
              <div className="px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color: "rgba(110,103,90,0.72)" }}>
                {n.section}
              </div>
            )}
            <NavLink to={n.to} end={n.to === homePath} data-testid={n.tid} onClick={onNavigate}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm mb-0.5 transition"
              style={({ isActive }) => linkStyle(isActive)}>
              <n.icon className="h-4 w-4 shrink-0" /><span className="truncate">{n.label}</span>
              <span title={n.badgeTitle || undefined} className="inline-flex">
                <UnreadBadge count={n.badge} testid={`${n.tid}-unread`} />
              </span>
            </NavLink>
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside data-testid={`${testidPrefix}-sidebar`}
        className="hidden lg:flex flex-col fixed top-0 left-0 h-screen w-64 z-40"
        style={{ background: CREAM, borderRight: `1px solid ${LINE}` }}>
        <div className="px-6 py-6 flex items-center gap-2.5" style={{ borderBottom: `1px solid ${LINE}` }}>
          <img src={logoMark} alt="AllRound" className="h-9 w-9 rounded-full object-cover shrink-0" />
          <div className="leading-none">
            <div className="lux-serif text-xl" style={{ color: INK }}>AllRound</div>
            <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: INK_SOFT }}>{subtitle}</div>
          </div>
        </div>
        <NavList />
        <NavSignOut testid={`${testidPrefix}-signout-btn`} />
        {footer && (
          <div className="px-6 py-4 text-[11px]" style={{ color: INK_SOFT, borderTop: `1px solid ${LINE}` }}>{footer}</div>
        )}
      </aside>

      {/* Mobile top bar */}
      <div data-testid={`${testidPrefix}-topbar`}
        className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 flex items-center justify-between px-4"
        style={{ background: CREAM, borderBottom: `1px solid ${LINE}` }}>
        <div className="flex items-center gap-2">
          <img src={logoMark} alt="AllRound" className="h-6 w-6 rounded-full object-cover" />
          <div className="lux-serif text-lg" style={{ color: INK }}>AllRound</div>
        </div>
        <div className="flex items-center gap-1">
          <NavSignOutIcon testid={`${testidPrefix}-signout-icon`} />
          <button type="button" data-testid={`${testidPrefix}-menu-btn`} onClick={() => setOpen(true)}
            className="p-2 rounded-lg" style={{ color: INK }}>
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {open && (
        <div data-testid={`${testidPrefix}-drawer`} className="lg:hidden fixed inset-0 z-50 bg-black/30"
          onClick={() => setOpen(false)}>
          <div className="absolute top-0 right-0 h-full w-72 flex flex-col" style={{ background: CREAM }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-4" style={{ borderBottom: `1px solid ${LINE}` }}>
              <div className="lux-serif text-lg" style={{ color: INK }}>Menu</div>
              <button type="button" data-testid={`${testidPrefix}-drawer-close`} onClick={() => setOpen(false)}
                className="p-2 rounded-full" style={{ color: INK }}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <NavList onNavigate={() => setOpen(false)} />
            <NavSignOut testid={`${testidPrefix}-signout-mobile-btn`} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      {/* Mobile bottom tab bar — persistent section switcher */}
      {tabs?.length > 0 && (
        <nav data-testid={`${testidPrefix}-tabbar`}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 flex"
          style={{ background: CREAM, borderTop: `1px solid ${LINE}` }}>
          {tabs.map((t) => {
            const isActive = t.id === activeTabId;
            return (
              <Link key={t.id} to={t.to} data-testid={t.tid}
                className="flex-1 flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold"
                style={{ color: isActive ? INK : INK_SOFT }}>
                <span className="relative">
                  <t.icon className="h-5 w-5" style={{ color: isActive ? GOLD : "currentColor" }} />
                  {t.badge > 0 && (
                    <span className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full"
                      style={{ background: GOLD }} data-testid={`${t.tid}-dot`} />
                  )}
                </span>
                {t.label}
              </Link>
            );
          })}
        </nav>
      )}
    </>
  );
}
